import React, { useCallback, useMemo, useState } from 'react';
import { Tooltip } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import InfoIconImageUrl from '@assets/icons/certificates/info.svg';

import { Currency } from '@components';
import { BlueButton } from '@share/components';

import { ItemTypeEnum } from '@share/common-types';
import { getAveragePriceLabel, getCertificateImageSrc } from './utils';
import { SavingsBadge, CertificateItemTag, EligibleCruiseLinesModal } from './components';
import type { CertificateCardProps } from './types';

import styles from './styles.module.scss';

export const CertificateCard = (props: CertificateCardProps): React.ReactElement => {
  const intl = useIntl();
  const {
    item,
    imageUrl,
    currency = '', // will be $ at Currency component
    variant = 'default',
    onCardClick,
    onBuyCertificate,
  } = props;
  const [cruiseLinesModalOpen, setCruiseLinesModalOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  const isSummary = variant === 'summary';

  const handleCardClick = useCallback(() => {
    if (isSummary || !onCardClick) {
      return;
    }
    onCardClick();
  }, [onCardClick, isSummary]);

  const handleBuyClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onBuyCertificate?.();
    },
    [onBuyCertificate],
  );

  const isCruiseCertificate = item.itemTemplates.some((t) => t.type === ItemTypeEnum.Cruise);
  const isEligibleCruiseLinesExists =
    item.eligibleCruiseLines && item.eligibleCruiseLines.length > 0;

  const handleEligibleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setCruiseLinesModalOpen(true);
  }, []);

  const closeCruiseLinesModal = useCallback(() => setCruiseLinesModalOpen(false), []);

  // The modal is portalled out of the card, but React still bubbles its events through
  // this tree, so closing it would otherwise trigger the card's navigation handler.
  const stopPropagation = useCallback((e: React.MouseEvent) => e.stopPropagation(), []);

  const imgSrc = getCertificateImageSrc(imageUrl, imageError);
  const savingsAmount = item.savings ?? 0;
  const savingsAmountPercentage = Math.round(
    (savingsAmount / (item.averageRetailPrice ?? 0)) * 100,
  );
  const averagePrice = item.averageRetailPrice ?? 0;
  const averagePriceLabel = useMemo(
    () => getAveragePriceLabel(intl, item.itemTemplates ?? []),
    [intl, item.itemTemplates],
  );

  const showExpiry = !isSummary || item.validityMonths > 0;
  const expiryParagraph = showExpiry ? (
    <p className={styles.expiry}>
      <FormattedMessage id="certificates.card.expiry" values={{ months: item.validityMonths }} />
    </p>
  ) : null;

  return (
    <article
      className={`${styles.card} ${isSummary ? styles.cardSummary : ''}`}
      onClick={handleCardClick}
    >
      <div className={styles.imageWrap}>
        <img
          src={imgSrc}
          alt=""
          className={styles.image}
          loading="lazy"
          onError={() => setImageError(true)}
        />
      </div>
      {!isSummary && savingsAmountPercentage > 0 && (
        <SavingsBadge amount={savingsAmountPercentage} />
      )}
      <div className={styles.body}>
        <div className={styles.content}>
          <div className={styles.titleRow}>
            <h3 className={styles.title}>{item.name}</h3>
            {!isSummary && (
              <p className={styles.price}>
                <Currency currency={currency} />
                {(item.totalPrice ?? 0).toFixed(2)}
              </p>
            )}
          </div>

          {!isSummary && (
            <div className={styles.savingsBlock}>
              <p className={styles.savingsBlockTitle}>
                <FormattedMessage id="certificates.card.savingsIntro" />
              </p>
              {averagePrice > 0 && averagePriceLabel && (
                <p className={styles.averagePrice}>
                  {averagePriceLabel}{' '}
                  <Currency currency={currency} />
                  {averagePrice.toFixed(2)}
                </p>
              )}
              {savingsAmount > 0 && (
                <p className={styles.savingsInline}>
                  <FormattedMessage id="certificates.card.estimatedSavings" />{' '}
                  <span className={styles.savingsValue}>
                    <Currency currency={currency} />
                    {savingsAmount.toFixed(2)}
                  </span>
                  <Tooltip title={intl.formatMessage({ id: 'certificates.card.savingsTooltip' })}>
                    <span
                      className={styles.infoIcon}
                      aria-label={intl.formatMessage({ id: 'certificates.card.infoAria' })}
                    >
                      <img
                        className={styles.infoIconImage}
                        src={InfoIconImageUrl}
                        alt={intl.formatMessage({ id: 'certificates.card.infoAlt' })}
                        aria-hidden
                        width={14}
                        height={14}
                      />
                    </span>
                  </Tooltip>
                </p>
              )}
            </div>
          )}

          {item.itemTemplates?.length > 0 && (
            <div className={styles.tags}>
              {item.itemTemplates.map((certItem, index) => (
                <CertificateItemTag
                  key={
                    certItem?.id?.trim()
                      ? certItem.id
                      : `${certItem.type}-${certItem.nights}-${index}`
                  }
                  item={certItem}
                />
              ))}
            </div>
          )}

          {isSummary && expiryParagraph}

          {isSummary && (
            <div className={styles.summaryTotal}>
              <span className={styles.summaryTotalLabel}>
                <FormattedMessage id="certificates.card.total" />
              </span>
              <span className={styles.summaryTotalValue}>
                <Currency currency={currency} />
                {(item.totalPrice ?? 0).toFixed(2)}
              </span>
            </div>
          )}
        </div>

        {!isSummary && (
          <div
            className={`${styles.footer} ${
              isCruiseCertificate && isEligibleCruiseLinesExists ? styles.cruiseFooter : ''
            }`}
          >
            <div className={styles.footerBlock}>
              {expiryParagraph}
              <BlueButton className={styles.buyButton} onClick={handleBuyClick}>
                <FormattedMessage id="certificates.card.buy" />
              </BlueButton>
            </div>
            <div
              className={`${styles.footerBlock} ${
                !isCruiseCertificate || !isEligibleCruiseLinesExists ? styles.detailsFooter : ''
              }`}
            >
              {isCruiseCertificate && isEligibleCruiseLinesExists && (
                <button type="button" className={styles.eligibleLink} onClick={handleEligibleClick}>
                  <FormattedMessage id="certificates.card.eligibleCruiseLines" />
                </button>
              )}
              <button
                type="button"
                className={styles.eligibleLink}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCardClick();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCardClick();
                  }
                }}
              >
                <FormattedMessage id="certificates.card.viewDetails" />
              </button>
            </div>
          </div>
        )}
      </div>

      <div onClick={stopPropagation}>
        <EligibleCruiseLinesModal
          open={cruiseLinesModalOpen}
          onClose={closeCruiseLinesModal}
          cruiseLines={item.eligibleCruiseLines}
        />
      </div>
    </article>
  );
};
